import { Link } from "react-router-dom";
import { Scale } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { classifyImc, changeColor } from "@/lib/tracking";

export const ProgressSummary = ({ records, lastImc }) => {
  const { t } = useTranslation();
  if (!lastImc) return null;
  const current = records[0]?.weight;
  const initial = records[records.length - 1]?.weight;
  const totalLoss = initial && current ? initial - current : null;
  const min = 18.5 * (lastImc.height / 100) ** 2;
  const max = 24.9 * (lastImc.height / 100) ** 2;
  const status = current < min ? "lostTooMuch" : current > max ? "inProgress" : "idealReached";
  const classification = classifyImc(lastImc.imc);
  const rows = [["currentWeight", `${lastImc.weight} kg`], ...(initial ? [["initialWeight", `${initial} kg`]] : []), ["height", `${lastImc.height} cm`]];
  return <>
    {!!current && <div className="bg-card border border-border rounded-xl p-5" data-testid="progress-weight-status">
      <p className="text-xs text-muted-foreground mb-1">{t("progress.currentStatus")}</p>
      <p className="text-xl font-bold text-primary">{t(`progress.weightStatuses.${status}`)}</p>
      <p className="text-sm text-muted-foreground mt-1">{t(`progress.weightStatuses.${status}Msg`)}</p>
    </div>}
    <div className="bg-card border border-border rounded-xl p-5" data-testid="progress-imc-status">
      <p className="text-xs text-muted-foreground mb-1">{t("progress.imcStatus")}</p>
      <p className={`text-2xl font-bold ${classification.color}`}>{t(`imc.classifications.${classification.key}`)}</p>
      <p className="text-sm text-muted-foreground">{t("imc.imcLabel")}: {lastImc.imc}</p>
    </div>
    <div className="bg-card border border-border rounded-xl p-5 space-y-3">
      <h2 className="font-semibold flex items-center gap-2"><Scale className="w-4 h-4 text-primary" />{t("progress.imcStats")}</h2>
      {rows.map(([key, value]) => <div key={key} className="flex justify-between gap-3 text-sm border-b border-border/40 py-1.5">
        <span className="text-muted-foreground">{t(`progress.${key}`)}</span><strong data-testid={`progress-${key}`}>{value}</strong>
      </div>)}
      <div className="flex justify-between text-sm"><span>{t("imc.imcLabel")}</span><strong data-testid="progress-imc-value">{lastImc.imc}</strong></div>
      {totalLoss !== null && <div className="flex justify-between gap-3 text-sm"><strong>{t("progress.totalLost")}</strong><strong className={changeColor(-totalLoss)} data-testid="progress-total-loss">{totalLoss > 0 ? "-" : totalLoss < 0 ? "+" : ""}{Math.abs(totalLoss).toFixed(1)} kg</strong></div>}
      <div className="bg-primary/10 border border-primary/20 rounded-lg p-3" data-testid="progress-ideal-range"><p className="text-xs text-primary">{t("progress.idealRange")}</p><p>{min.toFixed(1)} – {max.toFixed(1)} kg</p></div>
      <Button asChild variant="outline" className="w-full border-primary/30 text-primary"><Link to="/imc" data-testid="progress-new-imc">{t("progress.calcNewImc")}</Link></Button>
    </div>
  </>;
};